import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

// Configuration for shift changes
const SHIFT_CHANGE_CONFIG = {
  morningHour: 6,  // 6 AM
  eveningHour: 18, // 6 PM
  shifts: [
    { name: "Morning Shift", start: 6, end: 18, color: "bg-yellow-50 border-yellow-200 text-yellow-800" },
    { name: "Evening Shift", start: 18, end: 6, color: "bg-indigo-50 border-indigo-200 text-indigo-800" }
  ]
};

/**
 * Component that displays the shift change status for admin users
 * Shows the next scheduled shift change and a countdown timer
 */
const ShiftChangeStatus = ({ inline = false }) => {
  const { user, hasRole } = useAuth();
  const [timeUntilNextShift, setTimeUntilNextShift] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [nextShiftTime, setNextShiftTime] = useState('');
  const [currentShift, setCurrentShift] = useState(null);
  const [nextShift, setNextShift] = useState(null);
  
  // Check if this is a staff user
  const isStaffUser = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));
  
  useEffect(() => {
    // Only show for staff users
    if (!isStaffUser) return;
    
    const calculateNextShiftTime = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentSecond = now.getSeconds();
      
      // Determine current shift
      let currentShiftInfo;
      let nextShiftInfo;
      
      if (currentHour >= SHIFT_CHANGE_CONFIG.morningHour && currentHour < SHIFT_CHANGE_CONFIG.eveningHour) {
        // Morning shift (6am-6pm)
        currentShiftInfo = SHIFT_CHANGE_CONFIG.shifts[0];
        nextShiftInfo = SHIFT_CHANGE_CONFIG.shifts[1];
        setNextShiftTime('6:00 PM Today');
      } else {
        // Evening shift (6pm-6am)
        currentShiftInfo = SHIFT_CHANGE_CONFIG.shifts[1];
        nextShiftInfo = SHIFT_CHANGE_CONFIG.shifts[0];
        // If it's after midnight, next shift is morning today, otherwise it's tomorrow
        setNextShiftTime(currentHour < SHIFT_CHANGE_CONFIG.morningHour ? '6:00 AM Today' : '6:00 AM Tomorrow');
      }
      
      setCurrentShift(currentShiftInfo);
      setNextShift(nextShiftInfo);
      
      // Determine next shift hour
      let nextShiftHour;
      let isNextDay = false;
      
      if (currentHour < SHIFT_CHANGE_CONFIG.morningHour) {
        nextShiftHour = SHIFT_CHANGE_CONFIG.morningHour;
      } else if (currentHour < SHIFT_CHANGE_CONFIG.eveningHour) {
        nextShiftHour = SHIFT_CHANGE_CONFIG.eveningHour;
      } else {
        nextShiftHour = SHIFT_CHANGE_CONFIG.morningHour;
        isNextDay = true;
      }
      
      // Calculate time until next shift
      let hoursUntilShift = nextShiftHour - currentHour;
      if (isNextDay) hoursUntilShift += 24;
      
      let minutesUntilShift = 0 - currentMinute;
      if (minutesUntilShift < 0) {
        hoursUntilShift -= 1;
        minutesUntilShift += 60;
      }
      
      let secondsUntilShift = 0 - currentSecond;
      if (secondsUntilShift < 0) {
        minutesUntilShift -= 1;
        secondsUntilShift += 60;
      }
      
      // Handle edge case adjustments
      if (minutesUntilShift < 0) {
        hoursUntilShift -= 1;
        minutesUntilShift += 60;
      }
      
      setTimeUntilNextShift({
        hours: hoursUntilShift,
        minutes: minutesUntilShift,
        seconds: secondsUntilShift
      });
    };
    
    // Calculate immediately
    calculateNextShiftTime();
    
    // Update countdown every second
    const intervalId = setInterval(calculateNextShiftTime, 1000);
    
    // Clean up interval
    return () => clearInterval(intervalId);
  }, [isStaffUser]);
  
  // Don't render anything for non-staff users
  if (!isStaffUser) return null;
  
  // Format the time remaining
  const formatTimeDigit = (digit) => digit.toString().padStart(2, '0');
  const formatShiftHours = (start, end) => {
    const formatHour = (hour) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const h = hour % 12 || 12;
      return `${h}${period}`;
    };
    return `${formatHour(start)} - ${formatHour(end)}`;
  };
  
  // Render inline compact version that shows all info
  if (inline) {
    return (
      <div className={`border rounded-lg p-3 h-full flex flex-col justify-center ${currentShift?.color || 'bg-blue-50 border-blue-200 text-blue-800'}`}>
        <div className="flex justify-between items-center">
          <div className="flex-shrink-0">
            <h3 className="text-xs font-semibold mb-1">Staff Shift Information</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
              <p className="font-medium">Current:</p>
              <p><span className="font-bold">{currentShift?.name || ''}</span> <span className="opacity-75">({formatShiftHours(currentShift?.start || 0, currentShift?.end || 0)})</span></p>
              
              <p className="font-medium">Next shift:</p>
              <p><span className="font-bold">{nextShift?.name || ''}</span> <span className="opacity-75">({formatShiftHours(nextShift?.start || 0, nextShift?.end || 0)})</span></p>
              
              <p className="font-medium">Change at:</p>
              <p className="font-mono">{nextShiftTime}</p>
            </div>
          </div>

          <div className="text-center ml-4">
            <p className="text-xs font-medium mb-0.5">Time Until Shift Change</p>
            <div className="font-mono font-bold text-lg">
              {formatTimeDigit(timeUntilNextShift.hours)}:
              {formatTimeDigit(timeUntilNextShift.minutes)}:
              {formatTimeDigit(timeUntilNextShift.seconds)}
            </div>
            <p className="italic text-xs">
              Auto-logout at shift change
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Render regular detailed version
  return (
    <div className={`rounded-lg p-4 mb-4 border ${currentShift?.color || 'bg-blue-50 border-blue-200 text-blue-800'}`}>
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-sm font-semibold">Staff Shift Information</h3>
          <div className="mt-2">
            <p className="text-xs font-medium">
              Current: <span className="font-bold">{currentShift?.name || 'Loading...'}</span>
              <span className="ml-2 opacity-75">({formatShiftHours(currentShift?.start || 0, currentShift?.end || 0)})</span>
            </p>
            <p className="text-xs font-medium mt-1">
              Next shift: <span className="font-bold">{nextShift?.name || 'Loading...'}</span>
              <span className="ml-2 opacity-75">({formatShiftHours(nextShift?.start || 0, nextShift?.end || 0)})</span>
            </p>
            <p className="text-xs mt-1">
              Change at: <span className="font-mono font-medium">{nextShiftTime}</span>
            </p>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-xs font-medium mb-1">Time Until Shift Change</p>
          <div className="font-mono text-lg font-bold">
            {formatTimeDigit(timeUntilNextShift.hours)}:
            {formatTimeDigit(timeUntilNextShift.minutes)}:
            {formatTimeDigit(timeUntilNextShift.seconds)}
          </div>
          <p className="text-xs mt-2 italic">
            Auto-logout at shift change
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShiftChangeStatus; 